import React from 'react'
import {
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import Papa from 'papaparse'
import { useSnackbar } from 'notistack'

/**
 * Allow only:
 * Malaysia (60)
 * Indonesia (62)
 * Singapore (65)
 *
 * digits only
 * length 8–15 total
 */
const PHONE_REGEX = /^(60|62|65)[0-9]{6,13}$/

/**
 * Parse and validate CSV
 * Throws error immediately if invalid phone found
 */
const parseCsvText = (text = '') => {
  const result = Papa.parse(text.trim(), {
    skipEmptyLines: true,
  })

  const data = result?.data || []

  if (data.length < 2) throw new Error('CSV must contain header and at least one row.')

  const header = data[0].map((v) => String(v).trim())

  const rows = []

  for (let i = 1; i < data.length; i++) {
    const columns = (data[i] || []).map((v) => String(v).trim())

    const phone = columns[0]

    if (!phone) throw new Error(`Row ${i + 1}: Phone number is empty.`)

    if (!PHONE_REGEX.test(phone))
      throw new Error(
        `Row ${i + 1}: Invalid phone number "${phone}". Only MY(60), ID(62), SG(65) numbers allowed. Digits only, no +.`,
      )

    rows.push({ phoneNumber: phone, allVariables: columns.slice(1) })
  }

  return { rows, columnCount: Math.max(header.length - 1, 0) }
}

export const CsvPreviewSection = ({ selectedTemplateOption, setRows, rows }) => {
  const { enqueueSnackbar } = useSnackbar()

  const bodyVariableCount = selectedTemplateOption?.bodyVariableCount || 0

  const dynamicButtons = (selectedTemplateOption?.buttons || []).filter(
    (button) => button.isDynamic,
  )

  const expectedColumnCount = bodyVariableCount + dynamicButtons.length

  const onSelectFile = async (event) => {
    const file = event?.target?.files?.[0]

    if (!file) return

    // reset file input so same file can be reselected
    event.target.value = null

    // reset state first
    setRows([])

    try {
      const content = await file.text()

      if (!content.trim()) return enqueueSnackbar('CSV file is empty.', { variant: 'error' })

      const { rows: parsedRows, columnCount: parsedColumnCount } = parseCsvText(content)

      if (expectedColumnCount !== parsedColumnCount) {
        const requirementParts = []

        if (bodyVariableCount > 0)
          requirementParts.push(`${bodyVariableCount} for template text variable(s)`)

        if (dynamicButtons.length > 0)
          requirementParts.push(
            `${dynamicButtons.length} for button variable(s) (${dynamicButtons
              .map((button) =>
                button.type === 'URL' && button.urlPattern
                  ? `${button.text}: fills {{1}} in ${button.urlPattern}`
                  : button.text,
              )
              .join(', ')})`,
          )

        const requirementText =
          requirementParts.length > 0 ? requirementParts.join(' + ') : 'no variables'

        return enqueueSnackbar(
          `The CSV must have ${expectedColumnCount} column(s) after Phone Number: ${requirementText}. ` +
            `Your CSV has ${parsedColumnCount} column(s) instead.`,
          { variant: 'error' },
        )
      }

      const rowsWithSplitVariables = parsedRows.map(({ allVariables, ...row }) => ({
        ...row,
        variables: allVariables.slice(0, bodyVariableCount),
        buttonVariables: allVariables.slice(bodyVariableCount),
      }))

      setRows(rowsWithSplitVariables)

      enqueueSnackbar('CSV uploaded successfully.', { variant: 'success' })
    } catch (error) {
      console.error(error)

      enqueueSnackbar(error.message || 'Failed to process CSV.', { variant: 'error' })
    }
  }

  const columnLabels = [
    ...Array.from({ length: bodyVariableCount }, (_, index) => `Variable ${index + 1}`),
    ...dynamicButtons.map((button) =>
      button.type === 'URL' && button.urlPattern
        ? `Button: ${button.text} (fills {{1}} in ${button.urlPattern})`
        : `Button: ${button.text}`,
    ),
  ]

  return (
    <>
      <Grid item xs={12}>
        <Button variant='outlined' component='label' disabled={!selectedTemplateOption}>
          Upload CSV
          <input type='file' hidden accept='.csv,text/csv' onChange={onSelectFile} />
        </Button>
      </Grid>
      {rows?.length > 0 && (
        <Grid item xs={12}>
          <Typography variant='h5'>CSV Data Preview</Typography>
          <TableContainer
            component={Paper}
            sx={{
              mt: 1,
              maxHeight: 420,
              border: '1px solid #ddd',
            }}
          >
            <Table stickyHeader size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Phone Number</TableCell>
                  {columnLabels.map((label, index) => (
                    <TableCell key={index}>{label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, rowIndex) => (
                  <TableRow key={`${row.phoneNumber}-${rowIndex}`}>
                    <TableCell>{row.phoneNumber}</TableCell>
                    {row.variables.map((value, colIndex) => (
                      <TableCell key={`v-${colIndex}`}>{value}</TableCell>
                    ))}
                    {row.buttonVariables.map((value, colIndex) => (
                      <TableCell key={`b-${colIndex}`}>{value}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      )}
    </>
  )
}
